import { createContext, useContext, type ComponentType, type ReactNode } from 'react'
import { Box, Button, Typography } from '@mui/material'
import type { Can, NavItem } from './components/AppShell'
import { Surface } from './components/primitives'
import { useHost } from './host'
import { tokens } from './theme'

/**
 * What one build of the application has that another does not: an admin
 * section, and what a key (rather than a person's token) sees.
 *
 * Kept as data handed to the component rather than a module swapped at
 * build time, so the admin code is in the package that has it and nowhere
 * else — a build without that package cannot contain it. Signing in is not
 * part of it: the application around the component does that, and passes
 * the token (`getToken`) or relies on its session cookie.
 */
/**
 * A section an edition adds to a person's shell: a group in the rail and
 * the routes behind it, e.g. the buyer's sourcing workspace. Between the
 * Supply chain and Admin groups, in the order given.
 */
export interface EditionSection {
  title: string
  /** The organisation feature the section belongs to; hidden where it is off. */
  feature?: 'eudr' | 'sourcing'
  nav: NavItem[]
  routes: (can: Can) => ReactNode[]
}

export interface Edition {
  /** Sections beyond the application's own; none in a partner's build. */
  sections?: EditionSection[]
  /** The rail's Admin group. A group with nothing visible in it disappears. */
  adminNav: NavItem[]
  /** Admin routes inside a person's shell, for what they may reach. */
  adminRoutes: (can: Can) => ReactNode[]
  /** What a partner's root key sees when it is the credential. */
  RootKeyApp: ComponentType
  /** What one of the platform's own keys sees. */
  PlatformKeyApp: ComponentType
}

/**
 * Signed in with a key rather than a user token. A key holds no regions or
 * plots, and this edition has none of the screens a key can use, so say
 * what to do instead of showing an empty shell.
 */
function KeyNotice() {
  const { onSignOut } = useHost()
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 6 }}>
      <Surface sx={{ width: 480, p: 8 }}>
        <Typography variant="h3" sx={{ mb: 3 }}>That is a key, not a user token</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          A root key (<code>pck_…</code>) only creates users. Exchange it for a user
          token, then sign in with that:
        </Typography>
        <Box
          component="pre"
          sx={{
            p: 3, mb: 5, borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100,
            font: `12px ${tokens.font.mono}`, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}
        >
          {'curl -X POST "$API/users" \\\n  -H "authorization: Bearer pck_…" \\\n  -H "content-type: application/json" -d \'{}\''}
        </Box>
        {onSignOut && <Button variant="outlined" onClick={onSignOut}>Sign out</Button>}
      </Surface>
    </Box>
  )
}

/** No admin section: what a partner's application runs. */
export const defaultEdition: Edition = {
  adminNav: [],
  adminRoutes: () => [],
  RootKeyApp: KeyNotice,
  PlatformKeyApp: KeyNotice,
}

const EditionContext = createContext<Edition>(defaultEdition)

export const EditionProvider = EditionContext.Provider

/** The edition the application was started with. */
export const useEdition = () => useContext(EditionContext)
